import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Plus, Trash2, Check, RotateCcw } from 'lucide-react';
import { recipes } from '../data/recipes';
import { useAuth } from '../context/AuthContext';
import { createClient } from '@supabase/supabase-js';

type Props = {
  onBack: () => void;
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

export default function AdminDietRecipesPage({ onBack }: Props) {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const checkAdminAndLoadRecipes = async () => {
      if (!user) return;

      try {
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('firebase_uid')
          .eq('firebase_uid', user.id)
          .maybeSingle();

        if (!adminData) {
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        setIsAdmin(true);

        const { data: dietRecipes } = await supabase
          .from('diet_recipes')
          .select('recipe_id');

        if (dietRecipes) {
          setSelectedRecipes(dietRecipes.map(r => r.recipe_id));
        }
      } catch (error) {
        console.error('Error loading admin data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminAndLoadRecipes();
  }, [user]);

  const handleToggleRecipe = async (recipeId: string) => {
    if (!isAdmin) return;

    const isSelected = selectedRecipes.includes(recipeId);
    let newSelected: string[];

    if (isSelected) {
      newSelected = selectedRecipes.filter(id => id !== recipeId);
      await supabase
        .from('diet_recipes')
        .delete()
        .eq('recipe_id', recipeId);
    } else {
      newSelected = [...selectedRecipes, recipeId];
      await supabase
        .from('diet_recipes')
        .insert([{ recipe_id: recipeId }]);
    }

    setSelectedRecipes(newSelected);
  };

  const handleResetAllRecipes = async () => {
    if (!isAdmin || !window.confirm('Êtes-vous sûr? Cette action supprimera toutes les recettes diététiques sélectionnées.')) return;

    setIsSaving(true);
    try {
      await supabase
        .from('diet_recipes')
        .delete()
        .neq('recipe_id', '');

      setSelectedRecipes([]);
    } catch (error) {
      console.error('Error resetting recipes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredRecipes = recipes.filter(recipe =>
    recipe.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16">
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </button>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-600 font-semibold mb-2">Accès refusé</p>
            <p className="text-gray-500">Vous n'avez pas les permissions d'administrateur</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="mb-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </button>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Gérer les Recettes Diététiques</h1>
          <p className="text-gray-600 text-sm mt-1">
            {selectedRecipes.length} recette{selectedRecipes.length !== 1 ? 's' : ''} sélectionnée{selectedRecipes.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 space-y-4">
          <div className="flex items-center gap-3 bg-gray-100 rounded-lg px-4 py-3">
            <Search className="h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder="Chercher une recette..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-gray-100 outline-none text-gray-900"
            />
          </div>

          {selectedRecipes.length > 0 && (
            <div className="flex gap-3">
              <button
                onClick={handleResetAllRecipes}
                disabled={isSaving}
                className="flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="h-4 w-4" />
                Réinitialiser tout
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRecipes.length > 0 ? (
            filteredRecipes.map(recipe => {
              const isSelected = selectedRecipes.includes(recipe.id);
              return (
                <div
                  key={recipe.id}
                  onClick={() => handleToggleRecipe(recipe.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 flex-1">{recipe.name}</h3>
                    {isSelected && (
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 ml-2" />
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">
                      <span className="font-medium">Catégorie:</span> {recipe.category}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Difficulté:</span> {recipe.difficulty}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Temps:</span> {recipe.prepTime} + {recipe.cookTime}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-8">
              <p className="text-gray-500">Aucune recette trouvée</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
